/* eslint-disable import/no-unresolved */
import useMediaQuery from '@mui/material/useMediaQuery'
import { Link as MuiLink, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import BlueArrowRight from '../../assets/images/icons/blueArrowRight.svg'
import PropTypes from 'prop-types'
import React from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'

import { BLUE_BASE } from '../../theme/variables/colors'
import { cleanString, hexToRgba } from '../../utils/functions'

const BannerContainer = styled('div')(({ theme }) => ({
  borderTop: `1px solid ${hexToRgba(BLUE_BASE, 0.4)}`,
  borderBottom: `1px solid ${hexToRgba(BLUE_BASE, 0.4)}`,
  marginTop: '40px',
  padding: '16px 0',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    padding: '13px 0',
  },
}))

const Title = styled(Typography)(({ theme }) => ({
  margin: '0 0 32px',
  paddingBottom: '24px',
  borderBottom: `3px solid ${BLUE_BASE}`,
  width: 'fit-content',
}))

const LinkContainer = styled('a')(({ theme }) => ({
  display: 'flex',
  textDecoration: 'none',
  marginTop: 'auto',
  marginBottom: 'auto',
  marginLeft: '48px',
}))

const LinkText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: BLUE_BASE,
  display: 'flex',
  alignItems: 'center',
}))

// Adjust Headshot sizing on mobile
const Headshot = styled(GatsbyImage)(({ theme }) => ({
  filter: 'drop-shadow(0px 3px 11px rgba(0, 0, 0, 0.24))',
  '& img': {
    borderRadius: '50%',
    height: '109px',
    width: '109px',
    maxWidth: '109px !important',
  },
  [theme.breakpoints.down('sm')]: {
    filter: 'drop-shadow(0px 2px 6px rgba(0, 0, 0, 0.24))',
    marginTop: '0',
    '& img': {
      height: '80px',
      width: '80px',
      maxWidth: '80px !important',
    },
  },
}))

const ManagerContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  columnGap: 10,
  '& > div:not(:first-of-type)': {
    marginLeft: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'space-around',
  },
}))

const LogoManagerContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '24px',
  marginBottom: '24px',
}))

const LogoImage = styled(GatsbyImage)(({ theme }) => ({
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    objectFit: 'contain !important',
  },
}))

const StyledSwiper = styled(Swiper)(({ theme }) => ({
  position: 'relative',
  overflow: 'visible',
  '& .swiper-slide': {
    width: 'auto !important',
  },
  // Mobile adjustments
  [theme.breakpoints.down('sm')]: {
    // Force each slide to take up half the width (adjust as needed)
    '& .swiper-slide': {
      width: '100px !important',
    },
  },
}))

const arrowStyles = { marginLeft: '16px' }

const InvestmentTeamBanner = ({
  managers = [],
  management_section_title,
  investmentTeamLogo,
  investmentTeamLink,
}) => {
  const isMobile = useMediaQuery('(max-width:600px)')
  const maxManagersLayout1 = 3
  const maxManagersLayout2 = 8
  const maxManagersMobile = 2

  const InvestmentTeamLink = () => (
    <MuiLink component={LinkContainer} href="#InvestmentTeam">
      <LinkText variant="body2">Learn More</LinkText>
      <img src={BlueArrowRight} style={arrowStyles} alt="blue arrow" />
    </MuiLink>
  )

  const InvestmentTeamSwiper = ({ isMobile }) => (
    <StyledSwiper
      slidesPerView="auto"
      spaceBetween={isMobile ? 2 : 4}
      loop={true}
    >
      {managers.map((manager) =>
        manager.photo ? (
          <SwiperSlide key={manager.id}>
            <MuiLink href={`#${cleanString(manager.url)}`}>
              <Headshot
                image={getImage(manager.photo.localAsset)}
                alt={manager.title}
              />
            </MuiLink>
          </SwiperSlide>
        ) : null
      )}
    </StyledSwiper>
  )

  const InvestmentTeamImages = () => (
    <ManagerContainer>
      {managers.map((manager) =>
        manager.photo ? (
          <MuiLink key={manager.id} href={`#${cleanString(manager.url)}`}>
            <Headshot
              image={getImage(manager.photo.localAsset)}
              alt={manager.title}
            />
          </MuiLink>
        ) : null
      )}
    </ManagerContainer>
  )

  return (
    <BannerContainer>
      <div>
        {management_section_title && (
          <Title variant="h3">{management_section_title}</Title>
        )}
      </div>
      <LogoManagerContainer>
        {investmentTeamLogo && (
          <LogoImage
            image={getImage(investmentTeamLogo.localAsset)}
            alt={investmentTeamLogo.description || 'Management Team Logo'}
          />
        )}
        {managers.length <=
        (isMobile ? maxManagersMobile : maxManagersLayout2) ? (
          <InvestmentTeamImages />
        ) : (
          <InvestmentTeamSwiper isMobile={isMobile} />
        )}
      </LogoManagerContainer>
      {investmentTeamLink && <InvestmentTeamLink />}
    </BannerContainer>
  )
}

InvestmentTeamBanner.propTypes = {
  managers: PropTypes.array,
  management_section_title: PropTypes.string,
  investmentTeamLogo: PropTypes.object,
  investmentTeamLink: PropTypes.string,
}

export default InvestmentTeamBanner
